import { endAt, get, limitToFirst, onValue, orderByKey, orderByValue, query, ref, runTransaction, serverTimestamp, set, startAfter, update, type DatabaseReference, type QueryConstraint } from 'firebase/database';
import { ShiritoriFirebaseAuth, ShiritoriFirebaseDb } from './firebase-app';
import type { DbPlayersDto, PlayerDto, PlayerTeam } from '../game/core/player.ts';
import { randomName, randomUnusedTeam, generateUniqueId } from '../util/index.ts';
import { type GameConfigDto, type GameDto } from '../game/core/game.ts';
import type { ListInfoDto, ShiritoriWordDto, ShiritoriWordGroup } from '@/game/core/shiritori-word.ts';
import type { PictureSetDto } from '@/game/picture-set.ts';

export const MAX_PLAYERS_PER_GAME = 10;
export const GAME_TTL_MS = 72 * 60 * 60 * 1000;
export const CLEANUP_CLOCK_SAFETY_MS = 60 * 1000;
export const CLEANUP_BATCH_SIZE = 25;

export enum DbCollection {
    Game = 'game',
    GameExpiration = 'gameExpiration',
    KanjiLookup = 'kanjiLookup',
    Word = 'word',

    /** High level list information */
    List = 'list',

    /** Set of words that a list contains */
    ListWordSet = 'listWordSet',

    PictureSet = 'pictureSet',
}

export enum StorageKey {
    ImageSet = 'image_set',
}

export async function singleValueAsync<T>(ref: DatabaseReference): Promise<T> {
    return new Promise((resolve) => {
        onValue(ref, (snapshot) => resolve(snapshot.val()), { onlyOnce: true });
    });
}

export function shiritoriRef(path: string) {
    return ref(ShiritoriFirebaseDb, path);
}

export function getPlayersRef(gameId: string, uid?: string) {
    return ref(ShiritoriFirebaseDb, `${DbCollection.Game}/${gameId}/players/${uid || ''}`);
}

export function getRootRef(gameId: string) {
    return ref(ShiritoriFirebaseDb, `${DbCollection.Game}/${gameId}`);
}

let _wordListMap: Record<string, ListInfoDto> = {} as Record<string, ListInfoDto>;
export async function fetchWordLists() {
    if (Object.keys(_wordListMap).length === 0) {
        const ref = shiritoriRef(DbCollection.List);
        const map = await singleValueAsync<Record<string, ListInfoDto>>(ref);
        _wordListMap = map;
    }

    return _wordListMap;
}

let _pictureSetMap: Record<string, PictureSetDto> = {} as Record<string, PictureSetDto>;
export async function fetchPictureSets() {
    if (Object.keys(_pictureSetMap).length === 0) {
        const ref = shiritoriRef(DbCollection.PictureSet);
        const map = await singleValueAsync<Record<string, PictureSetDto>>(ref);
        _pictureSetMap = map;
    }

    return _pictureSetMap;
}

export interface WordPage {
    nextCursor?: string;
    words: ShiritoriWordDto[];
}

export async function fetchWordPage(cursor?: string, pageSize = 100): Promise<WordPage> {
    const constraints: QueryConstraint[] = [
        orderByKey(),
    ];

    if (cursor) {
        constraints.push(startAfter(cursor));
    }

    constraints.push(limitToFirst(pageSize + 1));

    const ref = shiritoriRef(DbCollection.Word);
    const q = query(ref, ...constraints);
    const snapshot = await get(q);
    const values = snapshot.val() || {};

    const entries = Object.entries(values as Record<string, ShiritoriWordGroup>);
    const hasNextPage = entries.length > pageSize;
    const pageEntries = entries.slice(0, pageSize);

    return {
        words: pageEntries.flatMap(([, group]) => (
            Object.values(group).sort((left, right) => {
                const leftRank = left.rank > 0 ? left.rank : Number.MAX_SAFE_INTEGER;
                const rightRank = right.rank > 0 ? right.rank : Number.MAX_SAFE_INTEGER;
                return leftRank - rightRank;
            })
        )),
        nextCursor: hasNextPage ? pageEntries.at(-1)?.[0] : undefined,
    };
}

/**
 * Retrieves the players ref for a given game id
 */
export async function getPlayers(gameId: string) {
    const playersRef = getPlayersRef(gameId);
    return singleValueAsync<DbPlayersDto>(playersRef);
}

/**
 * Generates a new game id that is not currently in use
 */
export async function getUnusedGameId() {
    let attempts = 10;

    while (attempts > 0) {
        let newId = generateUniqueId();

        const gamesRef = ref(ShiritoriFirebaseDb, `${DbCollection.Game}/${newId}`);
        const refExists = await checkRefExists(gamesRef);

        if (!refExists) {
            return newId;
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts--;
    }
}

export async function gameExists(gameId: string) {
    const gamesRef = ref(ShiritoriFirebaseDb, `${DbCollection.Game}/${gameId}/meta/createdDate`);
    return checkRefExists(gamesRef);
}

export async function createGameExpiration(gameId: string, createdDate: number) {
    const expirationRef = ref(ShiritoriFirebaseDb, `${DbCollection.GameExpiration}/${gameId}`);
    await set(expirationRef, createdDate);
}

/** Repairs the cleanup index if game creation completed before its index write. */
export async function ensureGameExpiration(gameId: string) {
    const createdDateRef = ref(ShiritoriFirebaseDb, `${DbCollection.Game}/${gameId}/meta/createdDate`);
    const query = await get(createdDateRef);
    const createdDate = query.val() as number | null;

    if (typeof createdDate === 'number') {
        await createGameExpiration(gameId, createdDate);
    }
}

export function getGameCleanupCutoff(serverNow: number) {
    return serverNow - GAME_TTL_MS - CLEANUP_CLOCK_SAFETY_MS;
}

/** Removes a bounded batch of games older than 72 hours using Firebase's clock. */
export async function cleanupExpiredGames() {
    const localNow = Date.now();
    const serverOffsetRef = ref(ShiritoriFirebaseDb, '.info/serverTimeOffset');
    const serverOffset = await singleValueAsync<number>(serverOffsetRef);
    const serverNow = localNow + (typeof serverOffset === 'number' ? serverOffset : 0);
    const cutoff = getGameCleanupCutoff(serverNow);
    const expirationsRef = ref(ShiritoriFirebaseDb, DbCollection.GameExpiration);
    const expiredGamesQuery = query(
        expirationsRef,
        orderByValue(),
        endAt(cutoff),
        limitToFirst(CLEANUP_BATCH_SIZE),
    );
    const snapshot = await get(expiredGamesQuery);
    const expiredGames = (snapshot.val() || {}) as Record<string, number>;

    await Promise.allSettled(Object.keys(expiredGames).map((gameId) => update(ref(ShiritoriFirebaseDb), {
        [`${DbCollection.Game}/${gameId}`]: null,
        [`${DbCollection.GameExpiration}/${gameId}`]: null,
    })));
}

export async function createGame(gameId: string, config: GameConfigDto) {
    const gameRef = ref(ShiritoriFirebaseDb, `${DbCollection.Game}/${gameId}`);
    const result = await runTransaction(gameRef, (data) => {
        if (!data) {
            return {
                meta: {
                    createdDate: serverTimestamp() as unknown as number
                },
                players: {},
                config
            } satisfies GameDto;
        }
    });

    if (result.committed) {
        void ensureGameExpiration(gameId).catch((error) => {
            console.error('[ensureGameExpiration]', error);
        });
    }

    return result;
}

/**
 * Makes a quick check at a ref location to see if it exists without fetching any data
 */
export async function checkRefExists(ref: DatabaseReference): Promise<boolean> {
    return new Promise((resolve) => {
        onValue(ref, (snapshot) => resolve(snapshot.exists()), console.log, { onlyOnce: true });
    });
}

/**
 * Adds a player to the game
 */
export async function addPlayerToGame(gameId: string, playerName: string) {
    if (!playerName?.trim()) {
        playerName = randomName();
    }

    const uid = ShiritoriFirebaseAuth.currentUser?.uid;
    if (!uid) {
        throw new Error('User is not authenticated');
    } else {
        const players = await getPlayers(gameId) || {};
        const existingPlayer = players[uid];
        if (existingPlayer) {
            return existingPlayer;
        }

        const playerCount = Object.keys(players).length;
        if (playerCount >= MAX_PLAYERS_PER_GAME) {
            return;
        }

        const currentTeams = Object.values(players).map((player) => player.team) as PlayerTeam[];
        const team = randomUnusedTeam(currentTeams);
        const nextTurnOrder = Object.values(players).reduce(
            (highest, player) => Math.max(highest, player.turnOrder),
            -1,
        ) + 1;
        const playerRef = getPlayersRef(gameId, uid);

        // Each player writes only their own record. The transaction prevents a
        // second tab for the same anonymous user from replacing it.
        return runTransaction(playerRef, (data: PlayerDto | null) => data || {
            uid,
            team,
            name: playerName,
            turnOrder: nextTurnOrder,
        });
    }
}

export async function changePlayerTeam(gameId: string, uid: string, team: PlayerTeam) {
    const currentUid = ShiritoriFirebaseAuth.currentUser?.uid;
    if (!currentUid || currentUid !== uid) {
        throw new Error('Cannot change another player');
    }

    const playerRef = getPlayersRef(gameId, uid);
    return runTransaction(playerRef, (playerData: PlayerDto | null) => {
        if (playerData) {
            return { ...playerData, team };
        }
    });
}

export async function setPlayerReadyState(gameId: string, uid: string) {
    const currentUid = ShiritoriFirebaseAuth.currentUser?.uid;
    if (!currentUid || currentUid !== uid) {
        throw new Error('Cannot ready another player');
    }

    const playerRef = getPlayersRef(gameId, uid);
    return runTransaction(playerRef, (playerData: PlayerDto | null) => {
        if (playerData) {
            return { ...playerData, ready: true };
        }
    });
}
